import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useRazorpay } from 'react-razorpay';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { formatApiErrorDetail, useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Checkout = () => {
  const { user } = useAuth();
  const { currency, formatPrice, isBaseCurrency, rateSource, updatedAt } = useCurrency();
  const [cart, setCart] = useState({ items: [] });
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const navigate = useNavigate();
  const { isLoading, Razorpay } = useRazorpay();

  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const fetchCart = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/cart`, { withCredentials: true });
      setCart(data);

      if (data.items && data.items.length > 0) {
        const productsData = await axios.get(`${BACKEND_URL}/api/products`);
        const productsMap = {};
        productsData.data.forEach((p) => {
          productsMap[p.id] = p;
        });
        setProducts(productsMap);
      } else {
        navigate('/cart');
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const calculateTotal = () => {
    return cart.items?.reduce((total, item) => {
      const product = products[item.product_id];
      return total + (product ? product.price * item.quantity : 0);
    }, 0) || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const totalAmount = calculateTotal();

      const { data: orderData } = await axios.post(
        `${BACKEND_URL}/api/orders/${paymentMethod === 'phonepe' ? 'create-phonepe-order' : 'create-razorpay-order'}`,
        {
          items: cart.items,
          total_amount: totalAmount,
          shipping_address: shippingAddress
        },
        { withCredentials: true }
      );

      if (paymentMethod === 'phonepe') {
        if (!orderData.redirect_url) {
          throw new Error('PhonePe checkout URL was not returned.');
        }
        window.location.href = orderData.redirect_url;
        return;
      }

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.order_id,
        name: 'Panchami Jewelry',
        description: 'Purchase from Panchami',
        handler: async (response) => {
          try {
            await axios.post(
              `${BACKEND_URL}/api/orders/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );
            navigate('/profile', {
              state: {
                paymentMessage: 'Payment successful! Your order has been placed.'
              }
            });
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Payment verification failed');
          } finally {
            setProcessing(false);
          }
        },
        prefill: {
          name: shippingAddress.name,
          contact: shippingAddress.phone,
          email: user?.email || ''
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            toast.error('Payment was cancelled before completion.');
          }
        },
        theme: {
          color: '#8C1B2F'
        }
      };

      const razorpayInstance = new Razorpay(options);
      razorpayInstance.on('payment.failed', (response) => {
        const reason = response?.error?.description || 'Payment failed. Please try again.';
        setProcessing(false);
        toast.error(reason);
      });
      razorpayInstance.open();
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="heritage-page min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c79a35]"></div>
      </div>
    );
  }

  return (
    <div className="heritage-page min-h-screen flex flex-col">
      <Header />

      <main className="heritage-shell flex-1">
        <h1 className="heritage-title mb-8 text-5xl" data-testid="checkout-title">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="heritage-card rounded-sm p-6" data-testid="checkout-form">
              <h2 className="heritage-title mb-6 text-3xl">Shipping Address</h2>

              <div className="mb-6">
                <p className="text-sm font-medium text-[#1A1A1A] mb-3">Payment Method</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('razorpay')}
                    className={`rounded-sm border px-4 py-3 text-left transition ${
                      paymentMethod === 'razorpay'
                        ? 'border-[#c79a35] bg-[#f7ecda]'
                        : 'border-[#dcc9ad] bg-[rgba(255,251,243,0.85)]'
                    }`}
                  >
                    <p className="font-medium text-[#1A1A1A]">Razorpay</p>
                    <p className="mt-1 text-sm text-[#5C5C5C]">Cards, net banking, UPI, wallets</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('phonepe')}
                    className={`rounded-sm border px-4 py-3 text-left transition ${
                      paymentMethod === 'phonepe'
                        ? 'border-[#8C1B2F] bg-[#FDF6F7]'
                        : 'border-[#E6E2D6] bg-white'
                    }`}
                  >
                    <p className="font-medium text-[#1A1A1A]">PhonePe</p>
                    <p className="mt-1 text-sm text-[#5C5C5C]">Hosted checkout with UPI intent, collect, QR, cards</p>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={shippingAddress.name}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                    required
                    className="heritage-input mt-2"
                    data-testid="checkout-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={shippingAddress.phone}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                    required
                    className="heritage-input mt-2"
                    data-testid="checkout-phone-input"
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={shippingAddress.address}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, address: e.target.value })}
                  required
                  className="heritage-input mt-2"
                  data-testid="checkout-address-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={shippingAddress.city}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                    required
                    className="heritage-input mt-2"
                    data-testid="checkout-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={shippingAddress.state}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                    required
                    className="heritage-input mt-2"
                    data-testid="checkout-state-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pincode">Pincode</Label>
                  <Input
                    id="pincode"
                    value={shippingAddress.pincode}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, pincode: e.target.value })}
                    required
                    className="heritage-input mt-2"
                    data-testid="checkout-pincode-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={processing || isLoading}
                className="heritage-button mt-6 w-full rounded-none"
                data-testid="place-order-btn"
              >
                {processing ? 'Processing...' : `Place Order & Pay with ${paymentMethod === 'phonepe' ? 'PhonePe' : 'Razorpay'}`}
              </Button>
              <p className="mt-3 text-sm text-[#6a5448]">
                After payment succeeds, you will be redirected to your profile order history.
              </p>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="heritage-card sticky top-24 rounded-sm p-6" data-testid="order-summary">
              <h2 className="heritage-title mb-6 text-3xl">Order Summary</h2>
              <div className="space-y-3">
                {cart.items?.map((item) => {
                  const product = products[item.product_id];
                  if (!product) return null;
                  return (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span>{product.name} x {item.quantity}</span>
                      <span>{formatPrice(product.price * item.quantity)}</span>
                    </div>
                  );
                })}
                <div className="heritage-divider mt-3 border-t pt-3">
                  <div className="flex justify-between font-heading text-xl">
                    <span>Total</span>
                    <span className="text-[#4b2e24]" data-testid="checkout-total">{formatPrice(calculateTotal())}</span>
                  </div>
                  {!isBaseCurrency && (
                    <p className="mt-3 text-xs text-[#6a5448]">
                      Prices are shown in {currency} using the latest available exchange data
                      {rateSource !== 'fallback' ? ` from ${rateSource}` : ''} 
                      {updatedAt ? ` as of ${new Date(updatedAt).toLocaleDateString()}` : ''}. Payment at checkout is processed in INR.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Checkout;
